import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import * as React from 'react';
import { cn } from '../lib/cn';

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 text-sm [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg+div]:pl-7',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground',
        info: 'border-info/40 bg-info/10 text-foreground',
        warning: 'border-warning/60 bg-warning/15 text-foreground',
        destructive: 'border-destructive/50 bg-destructive/10 text-foreground',
        success: 'border-success/50 bg-success/10 text-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

const ICONS = {
  default: Info,
  info: Info,
  warning: AlertTriangle,
  destructive: XCircle,
  success: CheckCircle2,
} as const;

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>, VariantProps<typeof alertVariants> {
  title?: React.ReactNode;
  hideIcon?: boolean;
}

export function Alert({
  className,
  variant = 'default',
  title,
  hideIcon,
  children,
  ...props
}: AlertProps) {
  const Icon = ICONS[variant ?? 'default'];
  return (
    <div
      role={variant === 'destructive' ? 'alert' : 'status'}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {!hideIcon && <Icon className="size-4" aria-hidden />}
      <div>
        {title && <h5 className="mb-1 font-medium leading-none tracking-tight">{title}</h5>}
        <div className="[&_p]:leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
